import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Search, FileText, AlertTriangle, Loader2, X } from "lucide-react";

const MODEL = "gemini-2.5-flash";

export default function Analyze() {
  const nav = useNavigate();
  const [tab, setTab] = useState("upload");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [ticker, setTicker] = useState("");
  const [uploadTicker, setUploadTicker] = useState("");
  const [stage, setStage] = useState(""); // "uploading", "analyzing"
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const runAnalysis = async (reportId) => {
    setStage("analyzing");
    try {
      const { data } = await api.post(`/reports/${reportId}/analyze`, { model: MODEL });
      if (data.status === "done") {
        toast.success("Analysis ready.");
        nav(`/app/report/${reportId}`);
        return;
      }
      const start = Date.now();
      while (Date.now() - start < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const { data: s } = await api.get(`/reports/${reportId}/status`);
          if (s.status === "done") {
            toast.success("Analysis complete.");
            nav(`/app/report/${reportId}`);
            return;
          }
          if (s.status === "error") {
            toast.error("AI analysis failed: " + (s.error || "unknown"));
            return;
          }
        } catch (_) {
          /* keep polling */
        }
      }
      toast.error("Analysis is taking too long. Check your dashboard in a minute.");
      nav("/app");
    } catch (err) {
      toast.error(errorMessage(err, "Analysis failed"));
      if (err.response?.status === 402) nav("/app");
    }
  };

  const validateAndSet = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are supported.");
      return;
    }
    if (f.size > 30 * 1024 * 1024) {
      toast.error("File too large (max 30MB).");
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer?.files?.[0];
    validateAndSet(dropped);
  };

  const uploadPdf = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Drop or pick a PDF first");
    setBusy(true);
    setStage("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (uploadTicker.trim()) fd.append("ticker", uploadTicker.trim().toUpperCase());
      // Title is optional; backend falls back to the filename.
      const { data } = await api.post("/reports/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await runAnalysis(data.id);
    } catch (err) {
      toast.error(errorMessage(err, "Upload failed"));
    } finally {
      setBusy(false);
      setStage("");
    }
  };

  const fetchTicker = async (e) => {
    e.preventDefault();
    if (!ticker.trim()) return toast.error("Enter a ticker");
    setBusy(true);
    setStage("uploading");
    try {
      const { data } = await api.post("/reports/fetch-ticker", { ticker });
      await runAnalysis(data.id);
    } catch (err) {
      toast.error(errorMessage(err, "Fetch failed"));
    } finally {
      setBusy(false);
      setStage("");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <Navbar />
      <div className="max-w-[1100px] mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">/ New Analysis</div>
        <h1 className="font-display font-black text-5xl tracking-tighter leading-none mb-8">Analyze a Report</h1>

        <div className="border-2 border-strong bg-white">
          <div className="flex border-b-2 border-strong">
            <button
              onClick={() => setTab("upload")}
              data-testid="tab-upload"
              className={`flex-1 px-6 py-4 text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 ${
                tab === "upload" ? "bg-black text-white" : "hover:bg-zinc-100"
              }`}
            >
              <Upload className="w-4 h-4" /> Upload PDF
            </button>
            <button
              onClick={() => setTab("ticker")}
              data-testid="tab-ticker"
              className={`flex-1 px-6 py-4 text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 border-l-2 border-strong ${
                tab === "ticker" ? "bg-black text-white" : "hover:bg-zinc-100"
              }`}
            >
              <Search className="w-4 h-4" /> Fetch by Ticker
            </button>
          </div>

          <div className="p-8">
            {tab === "upload" ? (
              <form onSubmit={uploadPdf} className="space-y-5">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    Drop your annual / quarterly report (PDF, max 30MB)
                  </label>
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    onClick={() => inputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    data-testid="upload-dropzone"
                    className={`mt-2 cursor-pointer border-2 border-dashed p-12 flex flex-col items-center justify-center text-center transition-colors ${
                      dragOver
                        ? "border-[#002FA7] bg-[#F0F4FF]"
                        : file
                          ? "border-strong bg-[#F4F4F5]"
                          : "border-strong bg-zinc-50 hover:bg-white"
                    }`}
                  >
                    <input
                      ref={inputRef}
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => validateAndSet(e.target.files?.[0])}
                      className="hidden"
                      data-testid="upload-file-input"
                    />
                    {file ? (
                      <>
                        <FileText className="w-10 h-10 mb-3" strokeWidth={1.5} />
                        <div className="text-sm font-bold break-all max-w-md">{file.name}</div>
                        <div className="text-xs text-zinc-500 mt-1">
                          {(file.size / 1024 / 1024).toFixed(2)} MB · ready to analyze
                        </div>
                        <button
                          type="button"
                          data-testid="upload-clear"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFile(null);
                            if (inputRef.current) inputRef.current.value = "";
                          }}
                          className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-[#FF2A00]"
                        >
                          <X className="w-3 h-3" /> Remove
                        </button>
                      </>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 mb-3" strokeWidth={1.5} />
                        <div className="text-base font-display font-bold tracking-tight">
                          Drag & drop your PDF here
                        </div>
                        <div className="text-xs text-zinc-500 mt-2">
                          or <span className="underline">click to browse</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    Optional · Ticker symbol (for live market price)
                  </label>
                  <input
                    data-testid="upload-ticker-input"
                    value={uploadTicker}
                    onChange={(e) => setUploadTicker(e.target.value.toUpperCase())}
                    className="w-full border-2 border-strong p-3 mt-1 focus:border-[#002FA7] outline-none uppercase font-mono-data"
                    placeholder="AAPL, RELIANCE, TCS.NS (leave blank to skip)"
                    maxLength={15}
                  />
                  <div className="text-xs text-zinc-500 mt-2">
                    If provided, we pull the current market price at analyze time and feed it to the DCF/Multiples engine — this lets the AI compute real upside/downside vs your live price.
                  </div>
                </div>
                <SubmitBtn busy={busy} stage={stage} label="Analyze PDF" testid="submit-pdf" />
              </form>
            ) : (
              <form onSubmit={fetchTicker} className="space-y-5">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Ticker Symbol</label>
                  <input
                    data-testid="ticker-input"
                    required
                    value={ticker}
                    onChange={(e) => setTicker(e.target.value.toUpperCase())}
                    className="w-full border-2 border-strong p-3 mt-1 focus:border-[#002FA7] outline-none uppercase"
                    placeholder="AAPL, RELIANCE, TCS.NS"
                  />
                  <div className="text-xs text-zinc-500 mt-2">
                    Pulls income statement, balance sheet, cash flow & overview from Alpha Vantage.
                  </div>
                </div>
                <SubmitBtn busy={busy} stage={stage} label="Fetch & Analyze" testid="submit-ticker" />
              </form>
            )}
          </div>
        </div>

        <div className="mt-8 border-l-4 border-[#FFC800] bg-yellow-50 p-4 flex gap-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-yellow-700 flex-shrink-0" />
          <div className="text-xs text-zinc-700 leading-relaxed">
            <b>Heads up:</b> Forensic analysis takes 30–120 seconds depending on report size. Stay on this page — you&apos;ll
            be redirected automatically when ready.
          </div>
        </div>
      </div>
    </div>
  );
}

function SubmitBtn({ busy, stage, label, testid }) {
  return (
    <button
      type="submit"
      disabled={busy}
      data-testid={testid}
      className="w-full bg-black text-white py-4 text-sm uppercase tracking-[0.2em] hover:bg-[#002FA7] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-3 shadow-brutal"
    >
      {busy ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          {stage === "uploading" ? "Processing report…" : "AI analyzing — this may take up to 2 minutes…"}
        </>
      ) : (
        label
      )}
    </button>
  );
}
